import {Injectable, inject} from '@angular/core'
import {ActivatedRouteSnapshot} from '@angular/router'
import {UveLogKey} from '@models/constants/log-key'
import {EmbeddedClientService} from '@service/embeddingService/embedded-client.service'
import {EmbeddedStoreService} from '@service/embeddingService/embedded-store.service'
import {EmbeddingInitService} from '@service/embeddingService/embedding-init.service'
import {LoggingService} from '@service/logging.service'
import {AuthGuardService} from './auth-guard.service'

/**
 * Guard de activación de rutas para el modo embebido.
 *
 * @description
 * Variante de `AuthGuardService` que no precarga el dominio completo ni valida
 * versión. En su lugar, según el tipo de embedding activo
 * (`embeddedService.type()`), inicializa únicamente los datos imprescindibles
 * para la vista concreta (`order::edit` vs `order::view`) y comunica al host
 * los eventos de loader (`loader::add`/`loader::remove`) y de pedido cargado
 * (`order::loaded`).
 *
 * Capa de Aplicación: orquesta la inicialización mínima en escenarios
 * embebidos donde el host controla la navegación y el ciclo de vida.
 *
 * @usageNotes
 *
 * ### Uso básico
 *
 * Registrado sólo en el bundle embebido (typicamente `src/app/app-embedded.ts`).
 *
 * ```typescript
 * {provide: AuthGuardService, useClass: AuthGuardEmbeddedService}
 * ```
 *
 * ### Best Practices
 *
 * - Añadir nuevos tipos de embedding requiere ampliar el `if/else` y
 *   coordinar la inicialización con `EmbeddingInitService`.
 * - Propagar siempre el evento `loader::remove` aun en error, para no
 *   dejar al host con un spinner indefinido.
 *
 * ### Bad Uses / Errores comunes
 *
 * - Usar este guard en el bundle web estándar: haría colapsar la app por
 *   faltar el contexto del host (mensajes, peerPayload).
 */
@Injectable({
  providedIn: 'root',
})
export class AuthGuardEmbeddedService extends AuthGuardService {
  private readonly embeddedService: EmbeddedClientService = inject(EmbeddedClientService)
  private readonly embeddedStoreService: EmbeddedStoreService = inject(EmbeddedStoreService)
  private readonly embeddedInitService: EmbeddingInitService = inject(EmbeddingInitService)

  override async canActivate(route: ActivatedRouteSnapshot): Promise<boolean> {
    this.embeddedService.sendMessage('loader::add', null)
    const isAuthenticated = await this.authService
      .authenticated()
      .then(async (authenticated: boolean) => {
        if (this.authService.firstOpen) {
          const embeddingType = this.embeddedService.type()
          if (embeddingType === 'order::edit') {
            await this.embeddedStoreService.initDataOrder(route.params, this.embeddedService.peerPayload()()?.data)
            await this.embeddedInitService.initOrderServices()
            this.embeddedService.sendMessage('order::loaded', null)
          } else if (embeddingType === 'order::view') {
            await this.embeddedStoreService.initDataOrdersDetail(
              route.params,
              this.embeddedService.peerPayload()()?.data
            )
            await this.embeddedInitService.initOrderViewServices(this.embeddedStoreService.productsImageByIdcProduct)
            this.embeddedService.sendMessage('order::loaded', null)
          } else {
            this.loggingService.error(UveLogKey.Error.EmbeddedGuardTypeNotAllowed, {embeddingType})
            return false
          }
        }

        this.authService.firstOpen = false
        return true
      })
      .catch(e => {
        this.loggingService.error(UveLogKey.Error.EmbeddedGuardCanActivate, LoggingService.formatError(e))
        return false
      })
    this.embeddedService.sendMessage('loader::remove', null)
    this.authService.notifierUserAuthenticated$ = isAuthenticated

    return isAuthenticated
  }
}
